import React from 'react';
import { FontIcon, mergeStyleSets } from '@fluentui/react';
import moment from 'moment';
import theme from '../../../theme';
import { IPurchaseOrder } from '../../../utils';

// Gedeelde bouwstenen voor het bovenste deel van het bestelbonvenster
// (bestelbonmodule en werffiche), zodat beide vensters gelijk blijven.

export const BRAND_PURPLE = '#581A73';

const classes = mergeStyleSets({
  // zelfde vlakke stijl als de sectiebalken "Details" en "Levering"
  card: {
    minWidth: 0,
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '0 0 12px',
    padding: '10px',
    background: theme.palette.neutralLight,
    color: 'black',
    fontSize: 16,
    fontWeight: 'bold',
  },
  cardBody: {
    padding: '0 10px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
    columnGap: 24,
    rowGap: 16,
    padding: '0 24px 16px',
  },
  info: {
    marginTop: 12,
    padding: '10px 12px',
    borderRadius: 2,
    background: theme.palette.neutralLighterAlt,
    fontSize: 13,
    lineHeight: '20px',
    color: theme.palette.neutralPrimary,
  },
  infoTitle: {
    fontWeight: 600,
  },
  infoMuted: {
    color: theme.palette.neutralSecondary,
  },
  callout: {
    marginTop: 12,
    padding: '10px 12px',
    borderRadius: 0,
    borderLeft: `3px solid ${theme.palette.themePrimary}`,
    background: theme.palette.themeLighterAlt,
    fontSize: 13,
    lineHeight: '20px',
    whiteSpace: 'pre-wrap',
  },
  calloutTitle: {
    display: 'block',
    marginBottom: 2,
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.themeDarker,
  },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    padding: '4px 24px 16px',
    fontSize: 13,
    color: theme.palette.neutralSecondary,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '2px 10px',
    borderRadius: 2,
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.white,
  },
});

export const SectionCard: React.FC<{ title: string; icon?: string }> = ({ title, icon, children }) => (
  <section className={classes.card}>
    <h3 className={classes.cardTitle}>
      {icon && <FontIcon iconName={icon} />}
      {title}
    </h3>
    <div className={classes.cardBody}>{children}</div>
  </section>
);

export const CardGrid: React.FC = ({ children }) => <div className={classes.grid}>{children}</div>;

export const InfoBox: React.FC<{ title?: string; lines: (string | React.ReactNode | undefined | null)[] }> = ({ title, lines }) => {
  const visible = lines.filter(line => line !== undefined && line !== null && line !== '' && line !== ' ');
  if (!title && visible.length === 0) return null;
  return (
    <div className={classes.info}>
      {title && <div className={classes.infoTitle}>{title}</div>}
      {visible.map((line, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i}>{line}</div>
      ))}
    </div>
  );
};

export const InfoMuted: React.FC = ({ children }) => <span className={classes.infoMuted}>{children}</span>;

export const SiteRemarksCallout: React.FC<{ text?: string | null }> = ({ text }) => (text ? (
  <div className={classes.callout}>
    <span className={classes.calloutTitle}>Werfopmerkingen (komen apart op de bon)</span>
    {text}
  </div>
) : null);

// Regel onder de titel: type, nummer, status en aanmaakdatum
export const PurchaseOrderMeta: React.FC<{ purchaseOrder?: IPurchaseOrder }> = ({ purchaseOrder }) => {
  const isOrder = purchaseOrder?.is_purchase_order !== false;
  const status = purchaseOrder?.status?.label || 'Nieuw';
  return (
    <div className={classes.meta}>
      <span className={classes.chip} style={{ background: isOrder ? theme.palette.themePrimary : BRAND_PURPLE }}>
        <FontIcon iconName={isOrder ? 'ShoppingCart' : 'Comment'} />
        {isOrder ? 'Bestelbon' : 'Prijsaanvraag'}
      </span>
      {purchaseOrder?.purchase_order_no ? <span>Nr. <strong>{purchaseOrder.purchase_order_no}</strong></span> : <span>Nog niet opgeslagen</span>}
      {purchaseOrder?.id ? <span>Status: <strong>{status}</strong></span> : null}
      {purchaseOrder?.creation_date ? (
        <span>Aangemaakt op {moment(purchaseOrder.creation_date).format('DD-MM-YYYY')}</span>
      ) : null}
    </div>
  );
};

// Zelfde logica als getContactLabel/getPhoneLabel/getFaxLabel in de vensters,
// maar als platte tekst, zodat lege waarden niet als lege regels verschijnen.
export const getContactInfo = (purchaseOrder?: IPurchaseOrder) => {
  const supplierContact: any = purchaseOrder?.supplier?.contact_persons?.[0];
  const chosen: any = purchaseOrder?.contact_person;
  const nameOf = (c: any) => (c ? [c.first_name, c.last_name].filter(Boolean).join(' ').trim() : '');
  const phoneOf = (c: any) => (c ? c.phone_v2 || c.mobile_v2 || c.phone || c.mobile || '' : '');
  let name = '';
  if (purchaseOrder?.supplier && supplierContact && purchaseOrder.is_default_contact_person) name = nameOf(supplierContact);
  else if (purchaseOrder?.supplier && chosen && !purchaseOrder.is_default_contact_person) name = nameOf(chosen);
  const phone = chosen ? phoneOf(chosen) : phoneOf(supplierContact);
  const fax = chosen ? chosen.fax || '' : supplierContact?.fax || '';
  return { name, phone, fax };
};
